import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import "./CountryPage.css";
import { getCountriesByContinent } from "../../api";

// Helper to convert buffer to base64 image
function bufferToBase64(buffer) {
  if (!buffer || !Array.isArray(buffer)) return null;
  const binary = String.fromCharCode(...buffer);
  return `data:image/png;base64,${btoa(binary)}`;
}

const PAGE_SIZE = 10;

const CountryPerContinetPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [continents, setContinents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await getCountriesByContinent({ page, pageSize: PAGE_SIZE, search });
        // Map flags for each country
        const mapped = data.map(continent => ({
          ...continent,
          countries: continent.countries.map(country => ({
            ...country,
            flag: country.Flag && country.Flag.data ? bufferToBase64(country.Flag.data) : null,
            flagText: (country.CountryID || country.code || country.id || "").toUpperCase()
          }))
        }));
        setContinents(mapped);
        setError("");
      } catch (err) {
        setError(t("Home Error Fallback"));
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [t, page, search]);

  const handleCountryClick = (country) => {
    navigate(`/cities/${country.CountryID || country.code || country.id}`, { state: { countryName: country.CountryName || country.name } });
  };

  // RTL support
  const isRTL = i18n.language === "ar";

  return (
    <main className={`page container country-page${isRTL ? " rtl" : ""}`}>
      <section>
        <div className="section-title">
          <h3>{t("Countries Page Title", "All Countries by Continent")}</h3>
        </div>
        <div style={{ marginBottom: 16 }}>
          <input
            type="text"
            placeholder={t("Countries Search Placeholder", "Search countries...")}
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            style={{
              padding: "8px 16px",
              borderRadius: "8px",
              border: "1px solid #ddd",
              fontSize: "1rem",
              width: "260px",
              direction: isRTL ? "rtl" : "ltr",
              textAlign: isRTL ? "right" : "left"
            }}
          />
        </div>
        {loading && (
          <p className="muted" style={{ marginBottom: 8 }}>
            {t("Home Loading", "Loading...")}
          </p>
        )}
        {error && (
          <p className="muted" style={{ marginBottom: 8, color: "#ffb3b3", fontSize: 13 }}>
            {error}
          </p>
        )}
        {continents.map(continent => (
          <div key={continent.continentID} className="continent-section">
            <h4 className="continent-title">{continent.Description}</h4>
            <div className="grid-countries">
              {continent.countries.map(country => (
                <div
                  key={country.CountryID || country.code || country.id}
                  className="country-card"
                  onClick={() => handleCountryClick(country)}
                  style={{ cursor: "pointer" }}
                >
                  {country.flag ? (
                    <img src={country.flag} alt={country.CountryName || country.name} className="flag-img" />
                  ) : (
                    <div className="flag-fallback">{country.flagText}</div>
                  )}
                  <div className="country-info">
                    <h4>{country.CountryName || country.name}</h4>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
        {/* Simple pagination controls */}
        <div style={{
          marginTop: 24,
          display: "flex",
          gap: 8,
          flexDirection: isRTL ? "row-reverse" : "row"
        }}>
          <button
            onClick={() => setPage(page > 1 ? page - 1 : 1)}
            disabled={page === 1}
            style={{
              padding: "8px 16px",
              borderRadius: "8px",
              border: "none",
              background: "#0077b6",
              color: "#fff",
              cursor: "pointer"
            }}
          >
            {t("Pagination Previous", "Previous")}
          </button>
          <span style={{ alignSelf: "center" }}>
            {t("Pagination Page", "Page")} {page}
          </span>
          <button
            onClick={() => setPage(page + 1)}
            style={{
              padding: "8px 16px",
              borderRadius: "8px",
              border: "none",
              background: "#0077b6",
              color: "#fff",
              cursor: "pointer"
            }}
          >
            {t("Pagination Next", "Next")}
          </button>
        </div>
      </section>
    </main>
  );
};

export default CountryPerContinetPage;